import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fileAPI } from '@/api/fileAPI';
import { FileMeta } from '@/types/files';
import toast from 'react-hot-toast';
import { MAX_UPLOAD_SIZE_BYTES, MAX_UPLOAD_SIZE_MB } from '@/utils/constants';

type FileScopeParams = {
  taskId?: string;
  source?: string;
  commentId?: string | null;
};

export const buildFileScopeKey = (params: FileScopeParams = {}) => {
  const { taskId, source, commentId } = params;
  const normalizedSource =
    source ?? (commentId ? 'comment' : 'attachment');

  return [
    taskId || 'global',
    normalizedSource || 'attachment',
    commentId || 'none',
  ].join('::');
};

const buildKeyFromFile = (file: FileMeta) =>
  buildFileScopeKey({
    taskId: file.task_id,
    source: file.source,
    commentId: file.comment_id,
  });

interface FileState {
  files: FileMeta[];
  loading: boolean;
  loadingByKey: Record<string, boolean>;
  error: string | null;
}

const initialState: FileState = {
  files: [],
  loading: false,
  loadingByKey: {},
  error: null,
};

const setScopeLoading = (
  state: FileState,
  key: string,
  value: boolean
) => {
  if (!key) {
    state.loading = value;
    return;
  }
  if (value) {
    state.loadingByKey[key] = true;
  } else {
    delete state.loadingByKey[key];
  }
  state.loading = Object.keys(state.loadingByKey).length > 0;
};

// ✅ Upload files (supports description / comment / attachment)
export const uploadFiles = createAsyncThunk(
  'files/upload',
  async (
    {
      files,
      taskId,
      source,
      commentId,
      uploadedBy,
      uploadedUserEmail,
    }: { 
      files: File[]; 
      taskId?: string; 
      source?: string; 
      commentId?: string;
      uploadedBy?: string;
      uploadedUserEmail?: string;
    },
    { rejectWithValue }
  ) => {
    const oversized = files.filter((file) => file.size > MAX_UPLOAD_SIZE_BYTES);

    if (oversized.length > 0) {
      const first = oversized[0];
      const sizeMb = (first.size / (1024 * 1024)).toFixed(2);
      const msg = `'${first.name}' is ${sizeMb}MB. Maximum allowed size is ${MAX_UPLOAD_SIZE_MB}MB. Please reduce the file size and try again.`;
      toast.error(msg);
      return rejectWithValue(msg);
    }

    try {
      const uploaded = await fileAPI.uploadFiles(
        files,
        taskId,
        source,
        commentId,
        uploadedBy,
        uploadedUserEmail
      );
      toast.success('Files uploaded successfully!');
      return uploaded.map((f) => ({
        ...f,
        source: source || 'attachment',
        comment_id: commentId || null,
      }));
    } catch (error: any) {
      const firstFile = files[0];
      const firstSizeMb = firstFile ? (firstFile.size / (1024 * 1024)).toFixed(2) : null;

      if (error.response?.status === 413) {
        const msg = firstFile
          ? `'${firstFile.name}' is ${firstSizeMb}MB. Maximum allowed size is ${MAX_UPLOAD_SIZE_MB}MB.`
          : `File size too large. Maximum allowed size is ${MAX_UPLOAD_SIZE_MB}MB.`;
        toast.error(msg);
        return rejectWithValue(msg);
      }

      if (error.code === 'ERR_FAILED' || error.message?.includes('ERR_FAILED')) {
        const msg = firstFile
          ? `File upload failed. '${firstFile.name}' might be too large (${firstSizeMb}MB). Maximum allowed size is ${MAX_UPLOAD_SIZE_MB}MB.`
          : 'Network error: File upload failed. Please check your connection and try again.';
        toast.error(msg);
        return rejectWithValue(msg);
      }

      let msg = 'Failed to upload files';
      if (error.response?.data?.detail) {
        msg = error.response.data.detail;
      } else if (error.response?.statusText) {
        msg = `Upload failed: ${error.response.statusText}`;
      } else if (error.message) {
        msg = `Upload failed: ${error.message}`;
      }
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

// ✅ Fetch files (filtered by source / comment)
export const fetchFiles = createAsyncThunk(
  'files/fetch',
  async (
    {
      taskId,
      source,
      commentId,
    }: { taskId?: string; source?: string; commentId?: string },
    { rejectWithValue }
  ) => {
    try {
      const files = await fileAPI.getFiles(taskId, source, commentId);
      return files.map((f) => ({
        ...f,
        source: f.source || source || 'attachment',
        comment_id: f.comment_id || commentId || null,
      }));
    } catch (error: any) {
      const msg = error.response?.data?.detail || 'Failed to fetch files';
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

// ✅ Delete file
export const deleteFile = createAsyncThunk(
  'files/delete',
  async (fileId: string, { rejectWithValue }) => {
    try {
      await fileAPI.deleteFile(fileId);
      toast.success('File deleted successfully');
      return fileId;
    } catch (error: any) {
      const msg = error.response?.data?.detail || 'Failed to delete file';
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

// ✅ Download file
export const downloadFile = createAsyncThunk(
  'files/download',
  async (
    { fileId, fileName }: { fileId: string; fileName?: string },
    { rejectWithValue }
  ) => {
    try {
      await fileAPI.downloadFile(fileId, fileName);
      toast.success('File downloaded successfully');
      return fileId;
    } catch (error: any) {
      const msg = error.response?.data?.detail || 'Failed to download file';
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

const fileSlice = createSlice({
  name: 'files',
  initialState,
  reducers: {
    clearFiles: (state) => {
      state.files = [];
      state.loadingByKey = {};
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadFiles.pending, (state, action) => {
        const key = buildFileScopeKey(action.meta.arg || {});
        setScopeLoading(state, key, true);
        state.error = null;
      })
      .addCase(uploadFiles.fulfilled, (state, action) => {
        const key = buildFileScopeKey(action.meta.arg || {});
        setScopeLoading(state, key, false);
        (action.payload as FileMeta[]).forEach((f) => {
          if (!state.files.find((x) => x._id === f._id)) {
            state.files.push(f as unknown as FileMeta);
          }
        });
      })
      .addCase(uploadFiles.rejected, (state, action) => {
        const key = buildFileScopeKey(action.meta.arg || {});
        setScopeLoading(state, key, false);
        state.error = action.payload as string;
      })
      .addCase(fetchFiles.pending, (state, action) => {
        const key = buildFileScopeKey(action.meta.arg || {});
        setScopeLoading(state, key, true);
        state.error = null;
      })
      .addCase(fetchFiles.fulfilled, (state, action) => {
        const key = buildFileScopeKey(action.meta.arg || {});
        setScopeLoading(state, key, false);

        const incoming = action.payload as FileMeta[];
        state.files = state.files.filter(
          (file) => buildKeyFromFile(file) !== key
        );
        incoming.forEach((file) => {
          if (!state.files.find((existing) => existing._id === file._id)) {
            state.files.push(file);
          }
        });
      })
      .addCase(fetchFiles.rejected, (state, action) => {
        const key = buildFileScopeKey(action.meta.arg || {});
        setScopeLoading(state, key, false);
        state.error = action.payload as string;
      })
      .addCase(deleteFile.fulfilled, (state, action) => {
        state.files = state.files.filter((f) => f._id !== action.payload);
      });
  },
});

export const { clearFiles } = fileSlice.actions;
export default fileSlice.reducer;
